import {
  MILL_DURATION, MILL_INPUT,
  MILL_INPUT_WHEAT, MILL_INPUT_CORN,
  MILL_OUTPUT_WHEAT, MILL_OUTPUT_CORN,
  SimState, Tile, XP_REWARDS,
} from './types';
import { ProgressionSystem } from './ProgressionSystem';
import { PlotSystem } from './PlotSystem';
import { ConstructionSystem } from './ConstructionSystem';
import { EventBus } from './events';

/** Per-kind balance — input cost from storage, output produced per batch. */
function inputCost(kind: 'wheat' | 'corn'): number {
  return kind === 'wheat' ? MILL_INPUT_WHEAT : MILL_INPUT_CORN;
}
function outputAmount(kind: 'wheat' | 'corn'): number {
  return kind === 'wheat' ? MILL_OUTPUT_WHEAT : MILL_OUTPUT_CORN;
}
function storageOf(state: SimState, kind: 'wheat' | 'corn'): number {
  return kind === 'wheat' ? state.res.basket : state.res.corn;
}
function spendInput(state: SimState, kind: 'wheat' | 'corn', n: number) {
  if (kind === 'wheat') state.res.basket -= n;
  else state.res.corn -= n;
}
function depositFlour(state: SimState, kind: 'wheat' | 'corn', n: number) {
  if (kind === 'wheat') state.res.flour += n;
  else state.res.cornFlour = (state.res.cornFlour ?? 0) + n;
}

/**
 * Mill processing — every mill tile owns its own queue of pending batches.
 * The HEAD entry is the currently-grinding batch; `millRemaining` counts
 * down its grind. When a batch finishes one unit of flour is deposited,
 * the head qty decrements, and the next batch (if any) starts. When qty
 * hits 0 the head entry is shifted off and the next kind takes over.
 *
 * Legacy single-batch fields (millRemaining, millInputUsed) still drive
 * the active-grind timer so animations / progress bars keep working.
 */
export const MillSystem = {
  /** True while a batch is actively grinding (queue head exists + timer > 0,
   *  OR queue head exists waiting to start). Used by the UI for the status
   *  read-out and by the auto-start short-circuit in Sim.clickTile. */
  isBusy(tile: Tile): boolean {
    const q = tile.millQueue ?? [];
    if (q.length > 0 && q[0].qty > 0) return true;
    return (tile.millRemaining ?? 0) > 0;
  },
  progress(tile: Tile): number {
    if ((tile.millRemaining ?? 0) <= 0) return 0;
    return 1 - (tile.millRemaining ?? 0) / MILL_DURATION;
  },

  /** Can the next queued batch (queue head) actually start right now? */
  canStart(state: SimState, tile: Tile): boolean {
    if (tile.bld !== 'mill') return false;
    if (!ConstructionSystem.isOperational(tile)) return false;
    if ((tile.millRemaining ?? 0) > 0) return false;
    const head = (tile.millQueue ?? [])[0];
    if (!head || head.qty <= 0) return false;
    if (storageOf(state, head.kind) < inputCost(head.kind)) return false;
    return true;
  },

  /** Append `qty` batches of `kind` to a mill's queue. If the mill is idle
   *  and the head batch can start, kick it off immediately. */
  enqueueBatch(
    state: SimState,
    c: number, r: number,
    kind: 'wheat' | 'corn',
    qty: number,
    bus: EventBus,
  ): boolean {
    if (qty <= 0) return false;
    const tile = PlotSystem.tile(state, c, r);
    if (!tile) return false;
    if (tile.bld !== 'mill') return false;
    if (!ConstructionSystem.isOperational(tile)) {
      bus.emit({ type: 'blocked', reason: 'Mill not yet operational' });
      return false;
    }
    if (!tile.millQueue) tile.millQueue = [];
    // Merge with the tail if same kind to keep the queue compact.
    const last = tile.millQueue[tile.millQueue.length - 1];
    if (last && last.kind === kind) last.qty += qty;
    else tile.millQueue.push({ kind, qty });
    bus.emit({ type: 'stateChanged' });
    // Kick off the head if nothing is currently grinding.
    if ((tile.millRemaining ?? 0) <= 0) {
      this.tryStartHead(state, tile, c, r, bus);
    }
    return true;
  },

  /** Legacy single-batch entry point — turns one click into "enqueue +1
   *  wheat batch". Kept so old callers (Sim.startMill, clickTile auto-start)
   *  still do the right thing. Spends MILL_INPUT wheat to match the
   *  original recipe. */
  start(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const tile = PlotSystem.tile(state, c, r);
    if (!tile) return false;
    if (tile.bld !== 'mill') return false;
    if (!ConstructionSystem.isOperational(tile)) return false;
    if ((tile.millRemaining ?? 0) > 0) {
      bus.emit({ type: 'blocked', reason: 'Mill already running' });
      return false;
    }
    if (state.res.basket < MILL_INPUT) {
      bus.emit({ type: 'blocked', reason: `Need ${MILL_INPUT} wheat` });
      return false;
    }
    return this.enqueueBatch(state, c, r, 'wheat', 1, bus);
  },

  /** Cancel every pending batch on a mill. Does NOT refund the input that
   *  was already consumed by the currently-grinding head batch — that
   *  flour is on its way and shouldn't vanish. */
  cancelQueue(state: SimState, c: number, r: number, bus: EventBus): boolean {
    const tile = PlotSystem.tile(state, c, r);
    if (!tile) return false;
    if (tile.bld !== 'mill') return false;
    if (!tile.millQueue || tile.millQueue.length === 0) return false;
    // If a batch is in flight, keep ONLY that one (the head, with qty=1) so
    // its already-spent input still becomes flour. Otherwise wipe entirely.
    if ((tile.millRemaining ?? 0) > 0 && tile.millQueue[0]) {
      tile.millQueue = [{ kind: tile.millQueue[0].kind, qty: 1 }];
    } else {
      tile.millQueue = [];
      tile.millRemaining = 0;
      tile.millInputUsed = 0;
    }
    bus.emit({ type: 'stateChanged' });
    return true;
  },

  /** Try to begin grinding the queue head. Consumes input + energy and
   *  primes `millRemaining`. Silent no-op when conditions fail (the UI
   *  reflects the wait state). */
  tryStartHead(
    state: SimState,
    tile: Tile,
    c: number, r: number,
    bus: EventBus,
  ): boolean {
    const head = (tile.millQueue ?? [])[0];
    if (!head || head.qty <= 0) return false;
    if ((tile.millRemaining ?? 0) > 0) return false;
    if (storageOf(state, head.kind) < inputCost(head.kind)) return false;
    spendInput(state, head.kind, inputCost(head.kind));
    tile.millRemaining = MILL_DURATION;
    tile.millInputUsed = inputCost(head.kind);
    ProgressionSystem.addXp(state, XP_REWARDS.startMill, bus);
    bus.emit({ type: 'millStarted', col: c, row: r });
    return true;
  },

  tick(state: SimState, dt: number, bus: EventBus) {
    PlotSystem.forEachTile(state, (tile, c, r) => {
      if (tile.bld !== 'mill') return;
      if (!ConstructionSystem.isOperational(tile)) return;   // skip while under construction
      // First: if a batch is grinding, advance it.
      if ((tile.millRemaining ?? 0) > 0) {
        tile.millRemaining = (tile.millRemaining ?? 0) - dt;
        if (tile.millRemaining! <= 0) {
          tile.millRemaining = 0;
          tile.millInputUsed = 0;
          // Resolve the head — deposit its flour, decrement qty, shift if drained.
          const q = tile.millQueue ?? [];
          const head = q[0];
          if (head) {
            const out = outputAmount(head.kind);
            depositFlour(state, head.kind, out);
            bus.emit({ type: 'millFinished', col: c, row: r, kind: head.kind, flour: out });
            head.qty -= 1;
            if (head.qty <= 0) q.shift();
          }
        }
      }
      // Then: if nothing is grinding and the queue has more, start the next.
      if ((tile.millRemaining ?? 0) <= 0) {
        const q = tile.millQueue ?? [];
        if (q.length > 0 && q[0].qty > 0) {
          this.tryStartHead(state, tile, c, r, bus);
        }
      }
    });
  },
};
